/*
 * biobio-bundle-selector-trio.js
 *
 * Web Component <bundle-selector-trio> que extende BundleSelector com
 * RESOLUCAO SERVER-SIDE da variante.
 *
 * Por que server-side (e nao o matching inline da base): o produto-bundle
 * tem MAIS de 250 variantes (ex: Trio Make Completa = Base 8 x Base 8 x BBS 6
 * = 384). O Shopify corta `product.variants` (Liquid) e `/products/<h>.js`
 * em 250 — entao o matching client-side (set OU multiset) NAO enxerga ~134
 * combinacoes e elas apareceriam como "indisponivel". Esse e exatamente o
 * caso do alerta do admin "tema pode nao ser compativel com +250 variantes".
 *
 * Solucao (padrao do tema Ollie, recomendada pelo Shopify pra +250 variantes):
 * manda os option-value-ids escolhidos via `?option_values=` e o Shopify
 * resolve a variante exata no servidor — inclusive as que passam de 250 e o
 * caso "mesmo tom nas duas bases" (cada eixo tem id proprio, sem ambiguidade).
 *
 * Mecanica:
 * - Swatches herdados (radios name="bundle-component-N", data-option-value=tom).
 *   `selectedByComponent[N] = tom` vem do applySelection da base.
 * - [data-trio-ovid-map] = { "N": { "<tom>": <option_value_id> } } por eixo,
 *   renderizado de um metafield. Mapeia tom -> id por eixo (a "Baru" da Base 1
 *   tem id diferente da Base 2).
 * - Ao completar a selecao: fetch /products/<handle>?option_values=id1,id2,id3,
 *   le o [data-bundle-variant-id] resolvido (= selected_or_first_available_variant
 *   que o Shopify setou) e poe no form do carrinho.
 * - Preco e ESTATICO (as 384 variantes tem o mesmo preco) -> nao mexe em preco;
 *   o de/por do CTA vem do Liquid (current_variant) e permanece.
 *
 * ISOLAMENTO: a classe-base e os outros bundles (Hidra/Make Glow/etc, todos
 * <=250 variantes) NAO sao tocados. So o Trio carrega este arquivo.
 */

class BundleSelectorTrio extends BundleSelector {
  connectedCallback() {
    // Inicializa ANTES do super: super.connectedCallback() ja chama o
    // loadProductData() (override abaixo) -> resolveBundleVariant(), entao
    // ovidMap/productUrl/_resolveSeq precisam existir antes, senao
    // Object.keys(this.ovidMap) lanca TypeError (unhandled rejection, pois
    // resolveBundleVariant e async chamado sem await) em todo primeiro load.
    // Os filhos ([data-trio-ovid-map]) e o dataset ja estao no DOM no connect
    // (JS carregado com defer, depois do parse do HTML do elemento).
    this.ovidMap = {};
    const mapEl = this.querySelector('[data-trio-ovid-map]');
    if (mapEl) {
      try {
        this.ovidMap = JSON.parse(mapEl.textContent);
      } catch (err) {
        console.error('[bundle-trio] ovid map parse falhou', err);
      }
    }
    this.productUrl = '/products/' + this.dataset.productHandle;
    this._resolveSeq = 0;

    super.connectedCallback();

    // Guard (dev): valida o bundle_ovid_map contra erros de autoria comuns.
    this._validateOvidMap();
  }

  // Sem lista de variantes inline (a base buscaria /products/<h>.js, capada em
  // 250 e inutil aqui). A resolucao real e server-side em resolveBundleVariant.
  loadProductData() {
    this.productCache = { variants: [] };
    this.resolveBundleVariant();
  }

  async resolveBundleVariant() {
    const map = this.ovidMap || {}; // guard: pode ser chamado antes do parse
    const chosen = this.selectedByComponent || {};
    const axisKeys = Object.keys(map).sort((a, b) => Number(a) - Number(b));

    // Incompleto: faltou escolher algum eixo. CTA fica em "Selecione um tom".
    const allChosen = axisKeys.length > 0 && axisKeys.every((k) => chosen[k]);
    if (!allChosen) {
      this.setCtaIncomplete();
      if (this.unavailableEl) this.unavailableEl.hidden = true;
      return;
    }

    // tom escolhido -> option_value_id, na ordem dos eixos.
    const ovids = [];
    for (const k of axisKeys) {
      const id = map[k] && map[k][chosen[k]];
      if (!id) {
        console.warn('[bundle-trio] sem option_value_id pra eixo', k, '/ tom', chosen[k]);
        this.setCtaReady(false);
        if (this.unavailableEl) this.unavailableEl.hidden = false;
        return;
      }
      ovids.push(id);
    }

    // Resolucao server-side. seq guard ignora respostas obsoletas se o usuario
    // troca de tom antes do fetch anterior voltar.
    //
    // &view=trio-resolve: template alternativo leve (templates/product.trio-resolve.liquid,
    // {% layout none %}) que renderiza SO o marker da variante resolvida -- payload
    // ~0.2KB em vez de baixar/parsear a pagina inteira do produto (~2.3MB), que era
    // o que deixava o CTA demorando ~1s (pior no mobile). A resolucao do option_values
    // e identica (mesmo pipeline de render do produto), so o template e enxuto.
    const seq = ++this._resolveSeq;
    const url = `${this.productUrl}?option_values=${ovids.join(',')}&view=trio-resolve`;
    try {
      const res = await fetch(url, { signal: this.abortController.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      if (seq !== this._resolveSeq) return; // selecao mudou; descarta

      const dom = new DOMParser().parseFromString(html, 'text/html');
      const idEl = dom.querySelector('[data-bundle-variant-id]');
      const marker = dom.querySelector('[data-trio-resolved]');
      const vid = (marker && marker.dataset.variantId) || (idEl && idEl.value);

      if (!vid) {
        this.setCtaReady(false);
        if (this.unavailableEl) this.unavailableEl.hidden = false;
        return;
      }

      // disponibilidade do combo resolvido (current_variant.available na pagina)
      const available = marker ? marker.dataset.available !== 'false' : true;

      // Guard C (dev): a variante resolvida tem EXATAMENTE os tons escolhidos?
      this._checkResolvedTones(marker, axisKeys.map((k) => chosen[k]));

      if (this.variantInput) this.variantInput.value = vid;
      this.setCtaReady(available);
      if (this.unavailableEl) this.unavailableEl.hidden = available;

      const u = new URL(window.location.href);
      u.searchParams.set('variant', vid);
      window.history.replaceState({}, '', u.toString());
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[bundle-trio] resolucao server-side falhou', err);
      // mantem CTA travado + mostra erro (mesmo modo da base em fetch failure)
      this.setCtaReady(false);
      if (this.unavailableEl) {
        this.unavailableEl.textContent = this.dataset.fetchError || 'Erro ao montar o combo. Recarregue a pagina.';
        this.unavailableEl.hidden = false;
      }
    }
  }

  // ---- Guards de DEV (so sinalizam erro de autoria; nao afetam o cliente) ----

  // Cada option_value_id pertence a UM eixo. Se o mesmo id aparece sob duas
  // chaves do ovid_map, os sub-mapas foram copiados sem trocar os ids (ex:
  // colou o eixo 1 no eixo 2) -> resolve variante real-mas-errada. Tambem
  // confere nº de eixos do map vs nº de cards com seletor.
  _validateOvidMap() {
    const map = this.ovidMap || {};
    const keys = Object.keys(map);
    const seen = {};
    const dupes = [];
    for (const k of keys) {
      for (const tone in map[k]) {
        const id = map[k][tone];
        if (seen[id] != null && seen[id] !== k) {
          dupes.push(`id ${id} nos eixos ${seen[id]} e ${k}`);
        } else {
          seen[id] = k;
        }
      }
    }
    const groups = this.querySelectorAll('.bundle-card__variant-selector[role="radiogroup"]').length;
    const problems = [];
    if (dupes.length) {
      problems.push(`bundle_ovid_map com id repetido entre eixos (${dupes.join('; ')}) -- sub-mapas colados sem trocar os ids?`);
    }
    if (keys.length && keys.length !== groups) {
      problems.push(`bundle_ovid_map tem ${keys.length} eixos mas ha ${groups} cards com seletor de tom (bundle_slots vs bundle_ovid_map fora de sincronia?)`);
    }
    if (problems.length) {
      const msg = '[bundle-trio] ' + problems.join(' | ');
      console.error(msg);
      this._devBanner(msg);
    }
  }

  // Compara os tons da variante que o servidor resolveu com os tons escolhidos.
  // Pega ovid_map desatualizado (ids regenerados ao recriar variantes) ou
  // trocado-mas-valido, que resolvem uma variante real porem errada.
  _checkResolvedTones(marker, chosenTones) {
    const raw = marker && marker.dataset.resolvedTones;
    if (!raw) return;
    const norm = (s) => (s || '').normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();
    const got = raw.split(' / ').map(norm);
    const want = chosenTones.map(norm);
    const mismatch = want.length !== got.length || want.some((t, i) => t !== got[i]);
    if (mismatch) {
      const msg = `[bundle-trio] RESOLUCAO INCONSISTENTE: escolhido [${chosenTones.join(', ')}] mas o servidor resolveu [${raw}]. Provavel bundle_ovid_map com id errado/desatualizado em algum eixo.`;
      console.error(msg);
      this._devBanner(msg);
    }
  }

  // Banner visivel SO no Theme Editor (data-design-mode="true"). Pro cliente
  // final os guards ficam so no console.
  _devBanner(msg) {
    if (this.dataset.designMode !== 'true') return;
    let el = this.querySelector('[data-trio-dev-banner]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-trio-dev-banner', '');
      el.style.cssText = 'margin:0 0 10px;padding:10px 12px;background:#fef2f2;border:1px dashed #fecaca;border-radius:8px;color:#b91c1c;font-size:13px;line-height:1.4;';
      this.prepend(el);
    }
    el.textContent = 'Aviso (so no editor): ' + msg;
  }
}

if (!customElements.get('bundle-selector-trio')) {
  customElements.define('bundle-selector-trio', BundleSelectorTrio);
}

// ---------------------------------------------------------------------------
// Fallback de add-to-cart pra LP (page template).
//
// Na PDP o tema converte o form do bundle em AJAX (theme.applyAjaxToProductForm
// -> remove o action do form + abre o cart drawer). Esse init do tema so roda
// em templates de produto/grid: em page templates (as LPs) NAO roda, entao o
// form do trio faz submit NATIVO pro /cart/add e o Yampi intercepta, jogando o
// cliente direto pro checkout em vez de adicionar ao carrinho.
//
// Aqui replicamos o add-to-cart nativo do tema (/cart/add.js +
// theme.updateCartSummaries) -- MESMO padrao ja comprovado pela guia em
// biobio-tone-finder.js. Delegacao no document (submit borbulha) com guard
// global. DEFERE pro tema sempre que ele ja assumiu o form (classe
// product-form-loaded ou action removido), entao e inerte na PDP.
(function () {
  if (window.__biobioBundleCartBound) return;
  window.__biobioBundleCartBound = true;

  function themeTookOver(form) {
    return form.classList.contains('product-form-loaded') || !form.getAttribute('action');
  }

  document.addEventListener('submit', async function (event) {
    const form = event.target;
    if (!form || !form.classList || !form.classList.contains('bundle-cta__form')) return;
    if (themeTookOver(form)) return; // PDP: o tema cuida do AJAX

    event.preventDefault();

    const idInput = form.querySelector('[name="id"]');
    const variantId = idInput && idInput.value;
    const btn = form.querySelector('[data-bundle-add-to-cart], button[name="add"]');
    if (!variantId || (btn && btn.disabled)) return;
    if (btn && btn.classList.contains('product-add--adding')) return; // anti duplo-clique

    if (btn) btn.classList.add('product-add--adding');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || 'HTTP ' + res.status);
      }

      const t = window.theme;
      const cartPageMode = !!(t && t.settings && t.settings.cartType === 'page');
      if (t && typeof t.updateCartSummaries === 'function' && !cartPageMode) {
        // bundle pede o drawer sempre (wrapper data-open-cart-drawer); 3o arg
        // false evita a distorcao do drawer (mesma nota da guia).
        t.updateCartSummaries(true, true, false);
        if (btn) {
          btn.classList.remove('product-add--adding');
          btn.classList.add('product-add--added');
          setTimeout(function () { btn.classList.remove('product-add--added'); }, 2000);
        }
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      console.error('[bundle-trio] add-to-cart fallback falhou', err);
      if (btn) btn.classList.remove('product-add--adding');
      window.location.href = '/cart';
    }
  }, false);
})();
